import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import PhotoLightbox from '../components/PhotoLightbox';
import HelpButton from '../components/HelpButton';

function currentDefaultYear() {
  const now = new Date();
  return now.getFullYear() + (now.getMonth() >= 9 ? 1 : 0);
}

export default function FamilyLettersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [year, setYear] = useState(currentDefaultYear());
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => { load(); }, [year]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await api.listLetters(year);
      setLetters(data.filter(l => l.user_id !== user?.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleReserve(itemId) {
    try {
      await api.reserveItem(itemId);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUnreserve(itemId) {
    try {
      await api.unreserveItem(itemId);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Cargando cartas... 🎄</div>;
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>← Volver</button>
        <HelpButton title="📜 Cartas de la familia">
          <p>Aquí ves las cartas de los demás miembros de la familia. Toca el nombre de alguien para desplegar su carta.</p>
          <p>Si ves un regalo que quieres comprarle, pulsa <strong>"Lo compro"</strong> — quedará tachado para que nadie más lo repita.</p>
          <p>Nadie sabrá que has sido tú quien lo reservó, excepto tú mismo (y puedes pulsar <strong>"Deshacer"</strong> en cualquier momento si cambias de opinión).</p>
          <p>👀 La persona dueña de la carta nunca ve qué está reservado en la suya, para mantener la sorpresa.</p>
        </HelpButton>
      </div>

      <div style={styles.yearSelector}>
        <button className="btn btn-ghost" onClick={() => setYear(y => y - 1)}>←</button>
        <h2 style={styles.year}>📜 Cartas familiares {year}</h2>
        <button className="btn btn-ghost" onClick={() => setYear(y => y + 1)}>→</button>
      </div>

      {error && <p className="error-msg" style={{ textAlign: 'center', marginBottom: '1rem' }}>⚠ {error}</p>}

      {letters.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No hay nadie más todavía.</p>
      ) : (
        <div style={styles.list}>
          {letters.map(letter => (
            <div key={letter.user_id} className="card" style={styles.letterCard}>
              <div
                style={styles.letterHeader}
                onClick={() => setExpandedId(expandedId === letter.user_id ? null : letter.user_id)}
              >
                <h3 style={{ color: 'var(--gold)', fontFamily: 'var(--font-display)' }}>{letter.display_name}</h3>
                <span style={{ color: 'var(--text-muted)' }}>{expandedId === letter.user_id ? '▲' : '▼'}</span>
              </div>

              {expandedId === letter.user_id && (
                <div style={styles.letterBody}>
                  {letter.free_text && (
                    <p style={styles.freeText}>{letter.free_text}</p>
                  )}

                  {letter.items.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Sin regalos en la lista todavía.</p>
                  ) : (
                    <div style={styles.itemsList}>
                      {letter.items.map(item => (
                        <div
                          key={item.id}
                          className="card"
                          style={{
                            ...styles.itemCard,
                            opacity: item.reserved && !item.reserved_by_me ? 0.5 : 1,
                          }}
                        >
                          {item.photo_url && (
                            <img
                              src={item.photo_url}
                              alt=""
                              style={{ ...styles.itemPhoto, cursor: 'pointer' }}
                              onClick={() => setLightboxSrc(item.photo_url)}
                            />
                          )}
                          <div style={styles.itemInfo}>
                            <p style={{
                              ...styles.itemDesc,
                              textDecoration: item.reserved ? 'line-through' : 'none',
                            }}>
                              {item.description}
                            </p>
                            {item.reserved_by_me && (
                              <span style={styles.reservedByMeTag}>Lo reservaste tú</span>
                            )}
                            {item.reserved && !item.reserved_by_me && (
                              <span style={styles.reservedTag}>Ya reservado por alguien</span>
                            )}
                          </div>
                          {!item.reserved && (
                            <button className="btn btn-primary" style={styles.actionBtn} onClick={() => handleReserve(item.id)}>
                              Lo compro
                            </button>
                          )}
                          {item.reserved_by_me && (
                            <button className="btn btn-ghost" style={styles.actionBtn} onClick={() => handleUnreserve(item.id)}>
                              Deshacer
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}

const styles = {
  page: { padding: '1rem' },
  yearSelector: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '1rem', marginBottom: '1.5rem',
  },
  year: { color: 'var(--gold)', fontSize: '1.1rem', minWidth: '200px', textAlign: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  letterCard: { padding: 0, overflow: 'hidden' },
  letterHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1rem 1.25rem', cursor: 'pointer',
  },
  letterBody: { padding: '0 1.25rem 1.25rem' },
  freeText: {
    color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6,
    background: 'var(--bg)', padding: '0.75rem', borderRadius: '8px',
    marginBottom: '1rem', whiteSpace: 'pre-wrap',
  },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  itemCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem' },
  itemPhoto: { width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
  itemInfo: { flex: 1 },
  itemDesc: { fontSize: '0.88rem', lineHeight: 1.4 },
  reservedTag: { color: 'var(--text-muted)', fontSize: '0.72rem', display: 'block', marginTop: '0.2rem' },
  reservedByMeTag: { color: 'var(--gold)', fontSize: '0.72rem', display: 'block', marginTop: '0.2rem' },
  actionBtn: { flexShrink: 0, fontSize: '0.78rem', padding: '0.4rem 0.7rem' },
};
